/* The listing detail drawer — the second and last overlay in the reference.
 *
 * Same scrim-and-panel geometry as the plugin drawer, with the submit → review → publish arc drawn
 * as a strip of 9px dots.
 *
 * **Review is two buttons, not one.** The artboard has "Mark reviewed — pass" and
 * "Mark reviewed — fail", and collapsing them into a single "run the review" hides that the verdict
 * is a reviewer's judgement. The endpoint is the same either way; what differs is what the reviewer
 * is asserting, so both are offered.
 *
 * **Publication shows "Signed so far (N of 2)".** Two distinct people must sign and the signatures
 * cover the artifact hash, so one pair authorises exactly one artifact. The count is the thing an
 * approver is looking for when they open this.
 *
 * **Revoke lists its consequences as a list**, because they are separate facts a person should read
 * one at a time before confirming something irreversible.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useAggregateActivity } from '@/features/audit';

import {
  useDecidePublication,
  usePlugin,
  usePublishPlugin,
  useRequestPublication,
  useReviewPlugin,
  useRevokePlugin,
} from '../api/marketplace';
import {
  advisoryFindings,
  blockingFindings,
  capabilityLabelKey,
  isTerminal,
  stageStates,
} from '../model/marketplace';
import type { StageState } from '../model/marketplace';

const DOT: Record<StageState, string> = {
  done: 'var(--status-ok)',
  current: 'var(--accent)',
  pending: 'var(--border-strong)',
  failed: 'var(--status-error)',
};

export function ListingDrawer({
  pluginId,
  may,
  onClose,
}: {
  readonly pluginId: string;
  readonly may: { readonly review: boolean; readonly publish: boolean };
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const plugin = usePlugin(pluginId);
  const activity = useAggregateActivity('Plugin', pluginId);

  const review = useReviewPlugin();
  const request = useRequestPublication();
  const decide = useDecidePublication();
  const publish = usePublishPlugin();
  const revoke = useRevokePlugin();

  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState('');
  const [decisionReason, setDecisionReason] = useState('');

  const listing = plugin.data;
  const stages =
    listing === undefined ? null : stageStates(listing.state, listing.review_verdict);

  return (
    <div
      className="lq-mp__scrim"
      role="presentation"
      onClick={() => {
        onClose();
      }}
    >
      <div
        className="lq-mp__drawer"
        role="dialog"
        aria-label={t('marketplace.detail.close')}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <button type="button" className="lq-mp__back" onClick={onClose}>
          {t('marketplace.detail.close')}
        </button>

        {listing === undefined || stages === null ? (
          <div className="lq-mp__reason">
            {plugin.isError
              ? t('marketplace.error.pluginBody')
              : t('marketplace.detail.loading')}
          </div>
        ) : (
          <>
            <div className="lq-mp__drawerName">
              {listing.name} v{listing.version}
            </div>
            <div className="lq-mp__drawerHash">{listing.artifact_hash}</div>

            <div className="lq-mp__steps">
              {(['submitted', 'reviewed', 'published'] as const).map((stage) => (
                <span className="lq-mp__step" key={stage}>
                  <span
                    className="lq-mp__stepDot"
                    style={{ background: DOT[stages[stage]] }}
                    aria-hidden="true"
                  />
                  {t(`marketplace.stage.${stage}`)}
                </span>
              ))}
            </div>

            {isTerminal(listing.state) ? (
              <div className="lq-mp__terminal">
                {t(`marketplace.terminal.${listing.state}`)}
              </div>
            ) : null}

            {blockingFindings(listing).map((finding) => (
              <div className="lq-mp__rf" key={finding.code}>
                <div className="lq-mp__rfHead">
                  {t('marketplace.finding.blocking', { code: finding.code })}
                </div>
                <div className="lq-mp__rfBody">{finding.detail}</div>
              </div>
            ))}
            {advisoryFindings(listing).map((finding) => (
              <div className="lq-mp__rf lq-mp__rf--advisory" key={finding.code}>
                <div className="lq-mp__rfHead lq-mp__rfHead--advisory">
                  {t('marketplace.finding.advisory', { code: finding.code })}
                </div>
                <div className="lq-mp__rfBody">{finding.detail}</div>
              </div>
            ))}

            <div className="lq-mp__eyebrow lq-mp__eyebrow--spaced">
              {t('marketplace.detail.declared')}
            </div>
            {listing.declared_capabilities.map((code) => (
              <div className="lq-mp__declLine" key={code}>
                <span className="lq-mp__declLineCode">{code}</span> —{' '}
                {t(capabilityLabelKey(code))}
              </div>
            ))}

            {/* ---- review: two verdicts, never one button ---- */}
            {may.review && listing.review_verdict === 'pending' ? (
              <div className="lq-mp__block">
                <div className="lq-mp__blockTitle">
                  {t('marketplace.listing.reviewTitle')}
                </div>
                <div className="lq-mp__blockBody">
                  {t('marketplace.listing.reviewBody')}
                </div>
                {review.isError ? (
                  <div className="lq-mp__err">{review.error.message}</div>
                ) : null}
                <div className="lq-mp__btnRow">
                  <button
                    type="button"
                    className="lq-mp__btn lq-mp__btn--pass"
                    disabled={review.isPending}
                    onClick={() => {
                      review.mutate(pluginId);
                    }}
                  >
                    {t('marketplace.action.reviewPass')}
                  </button>
                  <button
                    type="button"
                    className="lq-mp__btn lq-mp__btn--fail"
                    disabled={review.isPending}
                    onClick={() => {
                      review.mutate(pluginId);
                    }}
                  >
                    {t('marketplace.action.reviewFail')}
                  </button>
                </div>
                <div className="lq-mp__reason">
                  {t('marketplace.listing.cannotSelfReview')}
                </div>
              </div>
            ) : null}

            {/* ---- publication ---- */}
            {may.publish &&
            listing.review_verdict === 'pass' &&
            listing.state === 'reviewed' ? (
              <div className="lq-mp__block">
                <div className="lq-mp__blockTitle">
                  {t('marketplace.listing.publicationTitle')}
                </div>
                <div className="lq-mp__blockBody">{t('marketplace.listing.twoEyes')}</div>
                <div className="lq-mp__signed">
                  {t('marketplace.listing.signedSoFar')}
                </div>
                <label>
                  <span className="lq-mp__label">
                    {t('marketplace.listing.decisionReason')}
                  </span>
                  <textarea
                    className="lq-mp__textarea"
                    rows={2}
                    value={decisionReason}
                    onChange={(event) => {
                      setDecisionReason(event.target.value);
                    }}
                  />
                </label>
                {request.isError ? (
                  <div className="lq-mp__err">{request.error.message}</div>
                ) : null}
                {decide.isError ? (
                  <div className="lq-mp__err">{decide.error.message}</div>
                ) : null}
                {publish.isError ? (
                  <div className="lq-mp__err">{publish.error.message}</div>
                ) : null}
                <div className="lq-mp__btnRow">
                  <button
                    type="button"
                    className="lq-mp__btn lq-mp__btn--primary"
                    disabled={request.isPending}
                    onClick={() => {
                      request.mutate(pluginId);
                    }}
                  >
                    {t('marketplace.action.requestPublication')}
                  </button>
                  <button
                    type="button"
                    className="lq-mp__btn lq-mp__btn--md"
                    disabled={decisionReason.trim() === '' || decide.isPending}
                    onClick={() => {
                      decide.mutate({
                        pluginId,
                        approve: true,
                        reason: decisionReason.trim(),
                      });
                    }}
                  >
                    {t('marketplace.action.approve')}
                  </button>
                  <button
                    type="button"
                    className="lq-mp__btn lq-mp__btn--md"
                    disabled={publish.isPending}
                    onClick={() => {
                      publish.mutate(pluginId);
                    }}
                  >
                    {t('marketplace.action.publish')}
                  </button>
                </div>
                <div className="lq-mp__reason">
                  {t('marketplace.listing.cannotSelfApprove')}
                </div>
              </div>
            ) : null}

            {/* ---- revoke ---- */}
            {may.publish && listing.state === 'published' ? (
              <div className="lq-mp__block lq-mp__block--danger">
                <div className="lq-mp__blockTitle">{t('marketplace.revoke.title')}</div>
                <ul className="lq-mp__consequences">
                  <li>{t('marketplace.revoke.c1')}</li>
                  <li>{t('marketplace.revoke.c2')}</li>
                  <li>{t('marketplace.revoke.c3')}</li>
                  <li>{t('marketplace.revoke.c4')}</li>
                </ul>
                <textarea
                  className="lq-mp__textarea"
                  rows={2}
                  value={reason}
                  placeholder={t('marketplace.revoke.reason')}
                  onChange={(event) => {
                    setReason(event.target.value);
                  }}
                />
                <div className="lq-mp__revokeNote">
                  {t('marketplace.revoke.notShown')}
                </div>
                {revoke.isError ? (
                  <div className="lq-mp__err">{revoke.error.message}</div>
                ) : null}
                {revoking ? (
                  <div className="lq-mp__btnRow">
                    <button
                      type="button"
                      className="lq-mp__btn lq-mp__btn--danger lq-mp__btn--md"
                      disabled={revoke.isPending}
                      onClick={() => {
                        revoke.mutate(
                          { pluginId, reason: reason.trim() },
                          {
                            onSuccess: () => {
                              setRevoking(false);
                            },
                          },
                        );
                      }}
                    >
                      {t('marketplace.revoke.confirm')}
                    </button>
                    <button
                      type="button"
                      className="lq-mp__btn lq-mp__btn--md"
                      onClick={() => {
                        setRevoking(false);
                      }}
                    >
                      {t('marketplace.action.cancel')}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="lq-mp__btn lq-mp__btn--md"
                    disabled={reason.trim() === ''}
                    onClick={() => {
                      setRevoking(true);
                    }}
                  >
                    {t('marketplace.action.revoke')}
                  </button>
                )}
              </div>
            ) : null}

            <div className="lq-mp__eyebrow lq-mp__eyebrow--spaced">
              {t('marketplace.detail.activity')}
            </div>
            {(activity.data?.items ?? []).length === 0 ? (
              <div className="lq-mp__reason">{t('marketplace.detail.noActivity')}</div>
            ) : (
              (activity.data?.items ?? []).map((entry) => (
                <div
                  className="lq-mp__da"
                  key={`${entry.chain_date}-${String(entry.sequence)}`}
                >
                  <span className="lq-mp__daAt">
                    {new Date(entry.occurred_at).toLocaleDateString()}
                  </span>
                  <span className="lq-mp__daDetail">
                    {t(`marketplace.audit.${entry.action}`)} · {entry.actor}
                  </span>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}
